import { useCallback, useState } from "react";
import { serverErrorText } from "../lib/serverErrorText";
import {
  cancelCalcJob,
  getCalcJob,
  getCalcJobResult,
  startCalcJob,
} from "../lib/api";
import { toast } from "./ui/sonner";
import { useCalcJobPoll } from "./CalcProgressCard";
import { setCalcOutcomeNotice } from "../workflow/calcOutcomeNotice";   // A2-581 lead 4

/**
 * v0.6.8 — the BLOCKED outcome (unclassified/pending rows) used to be
 * an auto-dismissing toast.warning(), the same treatment as a plain
 * "3 items to review" notice. That's a real filing-blocking outcome,
 * not an FYI — this now returns it separately so the caller can show
 * it as a popup instead. The two non-blocking outcomes (review-only,
 * clean success) are still fine as toasts and unchanged.
 */
const classifyCalcResult = (result, t) => {
  if (!result) return null;
  const buckets = result.bucket_counts || {};
  const unclassified = Number(buckets.UNCLASSIFIED || 0);
  const pending = (result.pending_txn_ids || []).length;
  // A1-90: the stored job result keeps only the queue's first items; the count is whole.
  const reviewItems = result.review_queue_count ?? (result.review_queue || []).length;
  const total = result.transaction_count || 0;

  if (unclassified > 0 || pending > 0) {
    const desc =
      unclassified > 0 && pending > 0
        ? t("ui.calculate.toast_blocked_desc_pending", { unclassified, pending })
        : unclassified > 0
          ? t("ui.calculate.toast_blocked_desc", { unclassified })
          : t("ui.calculate.toast_pending_desc", { pending });
    const title =
      unclassified > 0
        ? t("ui.calculate.toast_blocked_title")
        : t("ui.calculate.toast_pending_title");
    return { blocked: true, title, desc };
  }
  if (reviewItems > 0) {
    toast.info(t("ui.calculate.toast_review_title"), {
      description: t("ui.calculate.toast_review_desc", { count: reviewItems }),
      duration: 8000,
    });
    return null;
  }
  toast.success(t("ui.calculate.toast_success_title"), {
    description: t("ui.calculate.toast_success_desc", { count: total }),
  });
  return null;
};

/**
 * v0.4.39 T1 — Async calc orchestration hook.
 *
 * Wraps `useCalcJobPoll` with the CalculatePanel-specific success +
 * failure handlers (advance the workflow stepper, propagate calc to
 * the WorkflowContext, surface error text). Lives in its own file
 * so CalculatePanel.jsx stays under the LOC cap.
 *
 * Returns:
 *   { job, elapsedMs, start(payload), cancel(), reset() }
 */
export const useAsyncCalc = ({ wf, onCalculated, setBusy, setError, t }) => {
  const [blockingMessage, setBlockingMessage] = useState(null);
  const poll = useCalcJobPoll({
    startFn: startCalcJob,
    getFn: getCalcJob,
    resultFn: getCalcJobResult,
    intervalMs: 2000,
    // v0.6 — cross-step-navigation guard (see WorkflowContext.jsx). Each
    // workflow step mounts its own CalculatePanel/useAsyncCalc instance;
    // without a registry shared via `wf` across all of them, a poll
    // orphaned by step navigation can't tell it's been superseded by a
    // re-run started from a different step's panel.
    jobRegistry: wf ? {
      register: wf.registerCalcJobStart,
      isCurrent: wf.isCurrentCalcJob,
    } : undefined,
    onComplete: (result) => {
      setBusy(false);
      onCalculated?.(result);
      // v0.6.6 fix — was wf?.setCalc?.(result), which doesn't pair
      // calcClientId with the new calc. WorkflowContext's auto-invalidate
      // effect then saw calcClientId !== clientId on the very next render
      // and wiped calc/review right after landing here on Step 4, showing
      // a blank Review screen. commitCalc keeps calc and calcClientId in
      // the same batch.
      wf?.commitCalc?.(result);
      // v0.5.2 fix — refresh the workflow-level review groups too.
      // CalculatePanel derives the Step-2 FX inputs (flaggedFxCurrencies)
      // from wf.review; without this refresh wf.review stayed stale/null
      // after a calc launched here, so per-row flagged currencies (e.g. a
      // USD row inside a EUR file) never appeared as a rate field — the
      // MISSING_EXCHANGE_RATE flag could never be cleared and the
      // "enter exchange rate" action looped forever.
      wf?.refreshReview?.(result?.id);
      // Always land the operator on the Review step (4) once a calc
      // completes — the review queue lives there. Previously this only
      // advanced from step 3, so a calc launched from step 1/2 left the
      // user stranded with a "resolve in the Review queue below" toast
      // and no queue in view.
      if (wf && (wf.activeStep || 0) < 4) wf.setActiveStep?.(4);
      try {
        const outcome = classifyCalcResult(result, t);
        if (outcome?.blocked) {
          setBlockingMessage({ severity: "error", title: outcome.title, body: outcome.desc });
          // A2-581 lead 4: the run moved to Review above, unmounting this
          // panel's dialog; the review page shows it instead
          setCalcOutcomeNotice(result?.id, outcome.title, outcome.desc);
        }
      } catch (e) { /* best-effort */ }
    },
    onFailure: (err) => {
      setBusy(false);
      // v0.6.12 — was JSON.stringify(err.detail): the operator got the raw
      // payload ({"error":"PERIOD_EMPTY",...}). The message is the part
      // written for them. See lib/serverErrorText.js.
      const msg = serverErrorText(err && err.detail,
        t("ui.calculate.failed", "Calculation failed"));
      setError(msg);
      // v0.6.8 — was toast.error(), an auto-dismissing corner toast for
      // a total job failure with zero results on screen; the operator
      // could easily miss it and believe the calc was still running.
      setBlockingMessage({
        severity: "error",
        title: t("ui.calculate.failed", "Calculation failed"),
        body: typeof msg === "string" ? msg : undefined,
      });
    },
  });

  const [pendingRerun, setPendingRerun] = useState(null);

  const cancel = useCallback(async () => {
    if (!poll.job?.job_id) return;
    try { await cancelCalcJob(poll.job.job_id); }
    catch (e) { /* swallow */ }
    setBusy(false);
  }, [poll.job?.job_id, setBusy]);

  // v0.6.10 — re-running REPLACES the current calc with a new one, and
  // review acknowledgements belong to the calc they were made against.
  // An operator who had worked through the queue and then re-ran lost
  // the lot, silently: the counters simply read as though nothing had
  // been done. Reported live on 2026-09-23 after 1,079 acknowledged
  // rows were stranded on the previous calc.
  //
  // The fix is a WARNING, deliberately, not an auto-carry-forward. An
  // acknowledgement is an operator attesting "I reviewed this row"; a
  // re-run can change a row's flags, so silently moving the
  // attestation onto the new calc would record them as having reviewed
  // something they never saw. That is a worse problem than the one it
  // solves, so the decision stays with the operator.
  const ackedCount = (wf?.review?.groups || [])
    .reduce((sum, g) => sum + (g.acknowledged_count || 0), 0);

  const guardedStart = useCallback((payload) => {
    // Only warn when there is something to lose: an existing calc that
    // carries acknowledgements. A first run, or a re-run of an
    // untouched queue, proceeds straight through.
    if (!wf?.calc?.id || ackedCount <= 0) return poll.start(payload);
    return new Promise((resolve) => {
      setPendingRerun({
        ackedCount,
        onProceed: () => { setPendingRerun(null); resolve(poll.start(payload)); },
        onCancel: () => {
          setPendingRerun(null);
          setBusy(false);      // the caller set it before calling start
          resolve(undefined);
        },
      });
    });
  }, [wf?.calc?.id, ackedCount, poll, setBusy]);

  return {
    ...poll, cancel, blockingMessage,
    dismissBlockingMessage: () => setBlockingMessage(null),
    // AFTER the spread — `poll` supplies its own `start`, and this
    // must win or the guard never runs.
    start: guardedStart,
    pendingRerun,
  };
};
